import { Suspense, lazy, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useMasterUser } from "@common/features/User";
import { __ } from "@common/helpers/i18n";
import { getPwaDisplayMode } from "@common/helpers/pwa";
import { LayoutComponentProps } from "@app/App";
import AppErrorBoundary from "@app/elements/errors/AppErrorBoundary";
import { useDocumentDirection } from "@app/hooks";
import { useUpdatePwa } from "@app/hooks/useUpdatePwa";
import { generateMenu } from "@app/menu";
import AppBadge from "./AppBadge";
import Footer from "./Footer";
import Header from "./Header";
import Menu from "./Menu";
import NavBar from "./Navbar";
import OfflineBanner from "./OfflineBanner";
import PwaUpdateModal from "./PwaUpdateModal";
import SiteNotification from "./SiteNotification";
import SubMenu from "./SubMenu";

const PwaPopUp = lazy(() => import("./PwaPopUp"));
const ReleaseNote = lazy(() => import("@app/layout/ReleaseNote"));

function UpdateOverlay() {
  return (
    <>
      <div
        className="pointer-events-none fixed inset-x-0 top-0 z-progress h-0.5 overflow-hidden bg-primary/10"
        aria-live="polite"
        aria-label={__("Update")}
      >
        <div
          className="h-full w-1/3 bg-primary"
          style={{
            animation: "pwa-update-progress 1.35s ease-in-out infinite",
          }}
        />
      </div>
      <style>{`
        @keyframes pwa-update-progress {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(400%);
          }
        }
      `}</style>
    </>
  );
}

export default function Main({ children, ...props }: LayoutComponentProps) {
  return (
    <AppErrorBoundary>
      <MainLayout {...props}>{children}</MainLayout>
    </AppErrorBoundary>
  );
}

function MainLayout({ isPublic, children }: LayoutComponentProps) {
  const {
    isUpdating,
    needRefresh: hasPwaUpdate,
    refresh: refreshPwa,
  } = useUpdatePwa();
  const masterUser = useMasterUser();
  const hasUser = !!masterUser;
  const location = useLocation();

  const appDir = useDocumentDirection();

  useEffect(() => {
    const syncPwaDisplayMode = () => {
      document.documentElement.dataset.pwaDisplayMode = getPwaDisplayMode();
    };
    const addDisplayModeListener = (query: MediaQueryList) => {
      if (typeof query.addEventListener === "function") {
        query.addEventListener("change", syncPwaDisplayMode);
        return;
      }

      query.addListener(syncPwaDisplayMode);
    };
    const removeDisplayModeListener = (query: MediaQueryList) => {
      if (typeof query.removeEventListener === "function") {
        query.removeEventListener("change", syncPwaDisplayMode);
        return;
      }

      query.removeListener(syncPwaDisplayMode);
    };
    const displayModeQueries = [
      window.matchMedia("(display-mode: fullscreen)"),
      window.matchMedia("(display-mode: standalone)"),
      window.matchMedia("(display-mode: minimal-ui)"),
    ];

    syncPwaDisplayMode();
    window.addEventListener("pageshow", syncPwaDisplayMode);
    displayModeQueries.forEach(addDisplayModeListener);

    return () => {
      window.removeEventListener("pageshow", syncPwaDisplayMode);
      displayModeQueries.forEach(removeDisplayModeListener);
    };
  }, []);

  useEffect(() => {
    if (location.hash) {
      return;
    }

    window.requestAnimationFrame(() => {
      document.getElementById("main-content")?.scrollTo({
        top: 0,
        left: 0,
        behavior: "instant",
      });
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    });
  }, [location.pathname, location.search, location.hash]);

  const isConversationPage =
    masterUser?.account_count !== 0 &&
    (location.pathname === "/comment" ||
      location.pathname.startsWith("/comment/") ||
      location.pathname === "/inbox" ||
      location.pathname.startsWith("/inbox/"));
  const isPostComposerPage =
    location.pathname === "/post" || location.pathname.startsWith("/post/");

  return (
    <>
      {isUpdating && <UpdateOverlay />}
      {!isPublic && (
        <Suspense fallback={null}>
          <PwaPopUp />
        </Suspense>
      )}
      {!isPublic && hasUser && <AppBadge />}
      <div
        className="flex h-[100dvh] min-h-0 flex-col overflow-hidden bg-background md:flex-row"
        dir={appDir}
      >
        {!isPublic && hasUser && (
          <>
            <NavBar />
            <Menu generateMenu={generateMenu} />
          </>
        )}
        <div
          className="flex h-full min-h-0 min-w-0 flex-1 flex-col"
          dir={appDir}
        >
          {!isPublic && hasUser && <Header />}
          {/* Bottom section */}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden md:flex-row">
            <div id="start-menu" className="min-h-0 flex" />
            <main className="relative min-h-0 min-w-0 flex-1 md:flex">
              <section
                id="main-content"
                aria-labelledby="primary-heading"
                dir={appDir}
                className={
                  "flex h-full min-w-0 flex-1 flex-col overflow-x-hidden overscroll-contain" +
                  (isConversationPage
                    ? " overflow-hidden"
                    : " overflow-y-auto") +
                  (hasUser ? " px-4 md:px-5 xl:px-6" : "")
                }
              >
                <Suspense fallback={null}>
                  {!isPublic && hasUser && (
                    <Suspense fallback={null}>
                      <ReleaseNote />
                    </Suspense>
                  )}
                  {!isPublic && hasUser && (
                    <OfflineBanner
                      className={
                        isConversationPage
                          ? "mx-0 mt-0 max-w-none px-4 py-3 md:px-5"
                          : undefined
                      }
                    />
                  )}
                  {!isPublic && hasUser && (
                    <PwaUpdateModal
                      isUpdating={isUpdating}
                      show={hasPwaUpdate}
                      onUpdate={refreshPwa}
                    />
                  )}
                  {!isPublic && hasUser && !isConversationPage && (
                    <SiteNotification />
                  )}
                  {!isPublic && hasUser && (
                    <SubMenu generateMenu={generateMenu} />
                  )}
                  {!isPublic && hasUser ? (
                    <div
                      className={
                        isConversationPage
                          ? "flex w-full min-h-0 min-w-0 flex-1 flex-col py-3.5 md:py-6"
                          : isPostComposerPage &&
                              masterUser?.account_count !== 0
                            ? "flex w-full min-h-0 min-w-0 flex-1 flex-col"
                            : "mx-auto flex w-full max-w-[1440px] min-h-0 min-w-0 flex-1 flex-col py-3.5 md:py-6"
                      }
                    >
                      {children}
                    </div>
                  ) : (
                    children
                  )}
                </Suspense>
              </section>
            </main>
            <div id="end-menu" className="flex overflow-hidden" />
          </div>
        </div>
        {!isPublic && hasUser && <Footer />}
      </div>
    </>
  );
}
